import type { SupabaseClient } from '../../lib/supabase';
import type { InboxItem, NotificationEvent } from './types';

/**
 * Database access for the Inbox and task mutes.
 *
 * There is no create path anywhere in the client: notifications are written
 * only by database triggers and the scheduled due-date job, and Row Level
 * Security scopes every read and write below to the signed-in person's own
 * rows. Nothing here is trusted for authorization.
 */

function fail(message: string): never {
  throw new Error(message);
}

/** The Inbox never needs everything ever received to be useful. */
export const INBOX_PAGE_SIZE = 100;

/**
 * An unread comment left through the public Huddle portal. RLS limits this to
 * the signed-in person's notification rows, exactly as it does for Inbox.
 */
export interface UnreadHuddleComment {
  id: string;
  task_id: string;
  comment_id: string;
  created_at: string;
}

const HUDDLE_UNREAD_PAGE_SIZE = 1000;

interface NotificationRow {
  id: string;
  event_type: NotificationEvent;
  project_id: string | null;
  task_id: string | null;
  comment_id: string | null;
  details: Record<string, unknown> | null;
  is_read: boolean;
  created_at: string;
  profiles: { display_name: string; email: string; avatar_url: string | null } | null;
  tasks: { title: string } | null;
  projects: { name: string } | null;
  cg_order_id: string | null;
  cg_orders: { order_no: string; order_type: string } | null;
  deal_id: string | null;
  social_post_id: string | null;
  /** npc r3: 'messages' or null; absent on a row from an older fixture. */
  source?: string | null;
}

/**
 * The signed-in person's Inbox, newest first, joined to what each item points
 * at. A join that comes back null means the target is gone or no longer
 * readable; the Inbox renders it as unavailable.
 */
export async function fetchInbox(client: SupabaseClient): Promise<InboxItem[]> {
  const { data, error } = await client
    .from('notifications')
    .select(
      // One literal, unwrapped (PostgREST type inference).
      // prettier-ignore
      'id, event_type, project_id, task_id, comment_id, cg_order_id, deal_id, social_post_id, source, details, is_read, created_at, profiles:actor_id (display_name, email, avatar_url), tasks:task_id (title), projects:project_id (name), cg_orders:cg_order_id (order_no, order_type)',
    )
    .order('created_at', { ascending: false })
    .limit(INBOX_PAGE_SIZE);

  if (error) {
    fail(error.message);
  }

  return ((data ?? []) as unknown as NotificationRow[]).map((row) => ({
    id: row.id,
    event_type: row.event_type,
    project_id: row.project_id,
    task_id: row.task_id,
    comment_id: row.comment_id,
    deal_id: row.deal_id,
    social_post_id: row.social_post_id,
    details: row.details ?? {},
    is_read: row.is_read,
    created_at: row.created_at,
    actor_name: row.profiles
      ? row.profiles.display_name.trim() !== ''
        ? row.profiles.display_name
        : row.profiles.email
      : '',
    actor_avatar_url: row.profiles?.avatar_url ?? null,
    task_title: row.tasks?.title ?? null,
    project_name: row.projects?.name ?? null,
    cg_order_id: row.cg_order_id,
    // Null when the order is gone or unreadable — a non-CG member holding an
    // old row sees the event without the order number, which is the same
    // "unavailable" shape the project join already has.
    cg_order_no: row.cg_orders?.order_no ?? null,
    // Same join, same null-when-unreadable shape. The badge is not drawn
    // rather than guessing a pipeline for an order nobody can read.
    cg_order_type: row.cg_orders?.order_type ?? null,
    // npc r3: which Inbox tab the row belongs to (isMessagesNotification).
    // null, never undefined, so a missing column reads as General.
    source: row.source ?? null,
  }));
}

/**
 * Every unread Huddle comment for the signed-in person, newest first.
 *
 * This is deliberately not derived from fetchInbox: Inbox is a 100-row view,
 * while the My Day strip is a count and must not silently lose older unread
 * rows. PostgREST caps a response at 1,000 rows, so this read walks every page.
 */
export async function fetchUnreadHuddleComments(
  client: SupabaseClient,
): Promise<UnreadHuddleComment[]> {
  const rows: UnreadHuddleComment[] = [];
  let from = 0;

  for (;;) {
    const { data, error } = await client
      .from('notifications')
      .select('id, task_id, comment_id, created_at')
      .eq('event_type', 'huddle_comment')
      .eq('is_read', false)
      .not('task_id', 'is', null)
      .not('comment_id', 'is', null)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .range(from, from + HUDDLE_UNREAD_PAGE_SIZE - 1);

    if (error) {
      fail(error.message);
    }

    const page = (data ?? []) as UnreadHuddleComment[];
    rows.push(...page);
    if (page.length < HUDDLE_UNREAD_PAGE_SIZE) return rows;
    from += page.length;
  }
}

/** One task's unread Huddle rows; RLS still bounds the write to the caller. */
export async function markTaskHuddleCommentsRead(
  client: SupabaseClient,
  taskId: string,
): Promise<void> {
  const { error } = await client
    .from('notifications')
    .update({ is_read: true })
    .eq('event_type', 'huddle_comment')
    .eq('is_read', false)
    .eq('task_id', taskId);

  if (error) {
    fail(error.message);
  }
}

/** is_read is the only writable column; read_at follows it by trigger. */
export async function setNotificationRead(
  client: SupabaseClient,
  notificationId: string,
  isRead: boolean,
): Promise<void> {
  const { error } = await client
    .from('notifications')
    .update({ is_read: isRead })
    .eq('id', notificationId);

  if (error) {
    fail(error.message);
  }
}

/** One statement; RLS bounds it to the caller's own rows. */
export async function markAllNotificationsRead(client: SupabaseClient): Promise<void> {
  const { error } = await client
    .from('notifications')
    .update({ is_read: true })
    .eq('is_read', false);

  if (error) {
    fail(error.message);
  }
}

/** Clearing a notification is deleting your own row (handoff 23). */
export async function deleteNotification(
  client: SupabaseClient,
  notificationId: string,
): Promise<void> {
  const { error } = await client.from('notifications').delete().eq('id', notificationId);
  if (error) {
    fail(error.message);
  }
}

/** Clear everything already read, in one statement. */
export async function clearReadNotifications(client: SupabaseClient): Promise<void> {
  const { error } = await client.from('notifications').delete().eq('is_read', true);
  if (error) {
    fail(error.message);
  }
}

/**
 * The task ids the signed-in person has muted. One small read for the whole
 * session; a task panel checks membership client-side.
 */
export async function fetchMutedTaskIds(client: SupabaseClient): Promise<string[]> {
  const { data, error } = await client.from('task_mutes').select('task_id');
  if (error) {
    fail(error.message);
  }
  return (data ?? []).map((row) => row.task_id);
}

export async function muteTask(
  client: SupabaseClient,
  input: { taskId: string; userId: string },
): Promise<void> {
  const { error } = await client.from('task_mutes').insert({
    task_id: input.taskId,
    user_id: input.userId,
  });

  // Muting an already-muted task is idempotent, not an error.
  if (error && !error.message.includes('task_mutes_task_user_key')) {
    fail(error.message);
  }
}

export async function unmuteTask(client: SupabaseClient, taskId: string): Promise<void> {
  const { error } = await client.from('task_mutes').delete().eq('task_id', taskId);
  if (error) {
    fail(error.message);
  }
}
